import {
  createEmptyBulkItemDraft,
  defaultBulkListingCommon,
  type BulkListingCommonState,
  type BulkListingItemDraft,
} from "@/lib/listing/bulkListingTypes";

const DRAFT_VERSION = 1 as const;
export const BULK_LISTING_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface SerializedBulkListingDraft {
  v: typeof DRAFT_VERSION;
  savedAt: number;
  common: BulkListingCommonState;
  items: Array<
    Omit<BulkListingItemDraft, "images" | "submitError" | "lastSubmitSucceeded">
  >;
}

function storageKey(userId: string): string {
  return `sell-bulk-draft-v${DRAFT_VERSION}-${userId}`;
}

export function saveBulkListingDraft(
  userId: string,
  common: BulkListingCommonState,
  items: BulkListingItemDraft[],
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: SerializedBulkListingDraft = {
      v: DRAFT_VERSION,
      savedAt: Date.now(),
      common,
      items: items.map(
        ({ images: _i, submitError: _e, lastSubmitSucceeded: _l, ...rest }) =>
          rest,
      ),
    };
    window.localStorage.setItem(storageKey(userId), JSON.stringify(payload));
  } catch {
    // Quota or privacy mode
  }
}

export function loadBulkListingDraft(
  userId: string,
): SerializedBulkListingDraft | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SerializedBulkListingDraft;
    if (parsed.v !== DRAFT_VERSION || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > BULK_LISTING_DRAFT_TTL_MS) {
      window.localStorage.removeItem(storageKey(userId));
      return null;
    }
    if (!parsed.common || !Array.isArray(parsed.items)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearBulkListingDraft(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}

export function restoredDraftToState(data: SerializedBulkListingDraft): {
  common: BulkListingCommonState;
  items: BulkListingItemDraft[];
} {
  const items: BulkListingItemDraft[] =
    data.items.length > 0
      ? data.items.map((row) => ({
          ...createEmptyBulkItemDraft(),
          ...row,
          images: [],
        }))
      : [createEmptyBulkItemDraft()];
  return {
    common: { ...defaultBulkListingCommon(), ...data.common },
    items,
  };
}
